// src/components/Help.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TopBar from './TopBar';

const DEFAULTS = {
  pageTitle: 'Help & Resources',
  pageSubtitle: 'Find answers to common questions and learn how to get the most out of your rewards system.',
  faqs: [
    { question: 'How do I set up a reward campaign?', answer: 'To set up a reward campaign, navigate to the Rewards page and click "Update Reward Setup". Then, define the reward details (for example, "Buy 10 coffees, get 1 free") and save your settings. This will launch your campaign immediately.' },
    { question: 'How do customers earn and redeem rewards?', answer: 'Customers collect a stamp for each purchase by scanning the QR code in your store. Once they accumulate the required number of stamps, they can claim their free reward via the mobile app.' },
    { question: 'How can I track reward performance?', answer: 'Your dashboard provides real-time metrics on total redemptions, customer activity, and overall campaign performance. Use these insights to optimize your offers.' },
  ],
  supportHeading: 'Need More Help?',
  supportText: "If you can't find what you're looking for, reach out to our support team and we'll get back to you as soon as possible.",
  supportEmail: 'support@rewarding.com',
};

const PageContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  overflow: 'hidden',
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing(4),
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

export default function Help() {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    fetch('/api/help-content')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && Object.keys(data).length > 1) {
          setContent({
            pageTitle: data.pageTitle || DEFAULTS.pageTitle,
            pageSubtitle: data.pageSubtitle || DEFAULTS.pageSubtitle,
            faqs: data.faqs && data.faqs.length > 0 ? data.faqs : DEFAULTS.faqs,
            supportHeading: data.supportHeading || DEFAULTS.supportHeading,
            supportText: data.supportText || DEFAULTS.supportText,
            supportEmail: data.supportEmail || DEFAULTS.supportEmail,
          });
        }
      })
      .catch(() => {});
  }, []);

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, textAlign: 'left', mb: 1, fontWeight: 700 }}>
          {content.pageTitle}
        </Typography>
        <Typography variant="body1" sx={{ mb: 5 }}>
          {content.pageSubtitle}
        </Typography>

        {/* FAQ Section */}
        <Box sx={{ mb: 4 }}>
          {content.faqs.map((faq, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Contact Support */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {content.supportHeading}
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
          {content.supportText}
        </Typography>
        <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
          Email: {content.supportEmail}
        </Typography>
      </ContentContainer>
    </PageContainer>
  );
}
